import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, timeout } from 'rxjs';
import { environment } from '../../../../environments/environment';
import Event from '../../../core/models/event';

/** One card as returned by v3's GET /events/public/list. */
interface V3EventCard {
  id: string;
  slug: string;
  title: string;
  startsAt: string;
  endsAt: string;
  cover: { url: string; alt: string | null } | null;
  categories: { id: number; name: string; emoji: string }[];
  vendor: { id: string; name: string };
  venue: string | null;
  pricing: { isFree: boolean; min: string; max: string; currency: string };
}

interface V3ListResponse {
  data: V3EventCard[];
}

/** v3 caps pageSize at 100; one page is plenty for the listing. */
const PAGE_SIZE = 100;

/** Don't let a slow v3 stall SSR of the whole homepage. */
const TIMEOUT_MS = 5000;

/**
 * Pulls the public event listing from v3 (v3.tix.do) and adapts it to this
 * app's Event model so the existing card renders it unchanged.
 *
 * Only Dominican Republic events come back: v3 resolves the country from the
 * request Host, so api.v3.tix.do is DO by definition — there is no country
 * param to send.
 *
 * Runs during SSR as well as in the browser, so nothing here may touch
 * window/localStorage/document.
 */
@Injectable({
  providedIn: 'root',
})
export class V3EventService {
  constructor(private http: HttpClient) {}

  getEvents(): Observable<Event[]> {
    return this.http
      .get<V3ListResponse>(
        `${environment.v3ApiUrl}/events/public/list?page=1&pageSize=${PAGE_SIZE}`
      )
      .pipe(
        map((resp) => (resp?.data ?? []).map((card, i) => this._map(card, i))),
        map((events) => events.filter((e): e is Event => e !== null)),
        timeout(TIMEOUT_MS),
        // v3 being down must never break this app's listing.
        catchError(() => of([]))
      );
  }

  private _map(card: V3EventCard, index: number): Event | null {
    // The card template has no image fallback, so a coverless event would
    // render broken — the legacy Strapi mapper drops these for the same reason.
    if (!card.cover?.url) return null;

    return {
      // Legacy ids are numbers and v3's are uuids; negative keeps them from
      // colliding with Strapi ids. Only used for *ngFor identity.
      id: -(index + 1),
      name: card.title,
      description: null,
      slug: card.slug,
      free: card.pricing.isFree,
      minPrice: Number(card.pricing.min),
      maxPrice: Number(card.pricing.max),
      startAt: card.startsAt as unknown as Date,
      endAt: card.endsAt as unknown as Date,
      hasAdd: false,
      applyFeed: false,
      category: {
        id: -1,
        // Where the name matches one of this app's categories it filters
        // correctly; where it doesn't the event just stays under "Todos".
        name: card.categories[0]?.name ?? 'Otros',
        description: null,
        priority: 0,
      },
      coverUri: card.cover.url,
      mapImageUri: null,
      twoDMapUri: null,
      place: card.venue ?? '',
      vendor: {
        id: -1,
        name: card.vendor.name,
        userFirstName: '',
        userLastName: '',
        userEmail: '',
        totalEvents: 0,
        active: true,
        profilePictureUri: '',
      },
      cover: [],
      externalUrl: `${environment.v3WebUrl}/events/${card.slug}`,
    };
  }
}
